import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, Repeat, Ticket, Sparkles, PartyPopper } from "lucide-react";
import { useIdioma } from "@/context/IdiomaContext";

const esOpenDay = (p) => (p.categoria || "").toUpperCase() === "OPEN DAY";
const esBono = (p) => !esOpenDay(p)
  && ((p.clases_incluidas || 0) > 0 || (p.categoria || "").toUpperCase() === "BONOS");
const PERIODOS = ["mensual", "trimestral", "semestral", "anual"];

const GRUPOS = [
  { id: "suscripciones", icono: Repeat,
    filtro: (p) => !esOpenDay(p) && !esBono(p) && PERIODOS.includes(p.tipo) },
  { id: "openday", icono: PartyPopper, filtro: esOpenDay },
  { id: "bonos", icono: Ticket, filtro: esBono },
  { id: "extras", icono: Sparkles,
    filtro: (p) => !esOpenDay(p) && !esBono(p) && !PERIODOS.includes(p.tipo) },
];

const PlanCard = ({ p, pagando, onPagar }) => (
  <Card className="rounded-2xl p-6 border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    data-testid={`portal-plan-${p.id}`}>
    <div className="flex flex-wrap gap-2 mb-3">
      <Badge variant="outline" className="rounded-full capitalize">
        {p.tipo === "unico" ? "Pago único" : p.tipo === "verano" ? "Curso de verano" : p.tipo}
      </Badge>
      {p.promocion && (
        <Badge className="rounded-full bg-emerald-600 hover:bg-emerald-600"
          data-testid={`portal-plan-promo-${p.id}`}>Promoción para ti</Badge>
      )}
      {p.hermanos > 1 && (
        <Badge className="rounded-full gap-1" data-testid={`portal-plan-hermanos-${p.id}`}>
          <Users className="h-3 w-3" /> {p.hermanos} hermanos
        </Badge>
      )}
    </div>
    <h3 className="font-display text-lg font-bold">{p.nombre}</h3>
    {p.descripcion && <p className="text-sm text-muted-foreground mt-2">{p.descripcion}</p>}
    <p className="font-display text-3xl font-extrabold mt-5">{p.importe} €</p>
    {p.hermanos > 1 && (
      <p className="text-xs text-muted-foreground mt-2">
        Cuota conjunta para {p.hermanos} hermanos ({(p.importe / p.hermanos).toFixed(2)} € por hijo/a)
      </p>
    )}
    {p.hermanos > 1 && p.ahorro_hermanos > 0 && (
      <p className="text-xs font-semibold text-emerald-600 mt-2" data-testid={`portal-plan-ahorro-${p.id}`}>
        Ahorras {p.ahorro_hermanos} € frente a dos cuotas individuales
      </p>
    )}
    {(p.clases_incluidas || 0) > 0 ? (
      <p className="text-xs text-muted-foreground mt-2">
        {p.clases_incluidas} clases{p.validez_meses ? ` · válido ${p.validez_meses} meses` : ""}
      </p>
    ) : (
      <p className="text-xs text-muted-foreground mt-2">
        {p.dias_semana > 1 ? `${p.dias_semana} días por semana` : "1 día por semana"}
      </p>
    )}
    {(p.matricula || p.seguro || p.block) && (
      <p className="text-xs text-muted-foreground mt-1" data-testid={`portal-plan-extras-${p.id}`}>
        {p.matricula ? (p.matricula_importe > 0
          ? `+ ${p.matricula_importe} € de matrícula (anual)` : "Matrícula incluida") : ""}
        {p.matricula && p.seguro ? " · " : ""}
        {p.seguro ? (p.seguro_importe > 0
          ? `+ ${p.seguro_importe} € de seguro de recuperación de clases (opcional, anual)`
          : "Seguro de recuperación de clases incluido") : ""}
        {p.block ? ` · + ${p.block_importe} € de block de dibujo (opcional)` : ""}
      </p>
    )}
    <Button className="rounded-full font-semibold w-full mt-6" disabled={pagando === p.id}
      onClick={() => onPagar(p)} data-testid={`portal-pagar-${p.id}`}>
      {pagando === p.id ? <Loader2 className="h-4 w-4 animate-spin" />
        : ["unico", "verano"].includes(p.tipo) ? "Pagar" : "Suscribirme"}
    </Button>
  </Card>
);

const Chip = ({ activo, children, ...props }) => (
  <button type="button"
    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
      activo ? "bg-primary text-primary-foreground border-primary"
        : "border-border text-muted-foreground hover:bg-muted"}`}
    {...props}>
    {children}
  </button>
);

/** Planes agrupados en Suscripciones (periodicidad → nº de hijos), Bonos y Extras. */
export const PortalPlanes = ({ planes = [], pagando, onPagar }) => {
  const { t } = useIdioma();
  const [grupo, setGrupo] = useState("suscripciones");
  const [periodo, setPeriodo] = useState("");
  const [hermanos, setHermanos] = useState(0);

  const porGrupo = useMemo(
    () => Object.fromEntries(GRUPOS.map((g) => [g.id, planes.filter(g.filtro)])), [planes]);
  const disponibles = GRUPOS.filter((g) => porGrupo[g.id].length > 0);
  const grupoActivo = porGrupo[grupo]?.length ? grupo : (disponibles[0]?.id || "suscripciones");
  const lista = porGrupo[grupoActivo] || [];

  const periodos = useMemo(
    () => PERIODOS.filter((tp) => lista.some((p) => p.tipo === tp)), [lista]);
  const periodoActivo = periodos.includes(periodo) ? periodo : (periodos[0] || "");
  const delPeriodo = grupoActivo === "suscripciones"
    ? lista.filter((p) => p.tipo === periodoActivo) : lista;

  const tallas = useMemo(
    () => [...new Set(delPeriodo.map((p) => p.hermanos || 1))].sort((a, b) => a - b), [delPeriodo]);
  const hermanosActivo = tallas.includes(hermanos) ? hermanos : (tallas[0] || 1);
  const segmentaPorHijos = grupoActivo === "suscripciones" || grupoActivo === "bonos";
  const visibles = segmentaPorHijos
    ? delPeriodo.filter((p) => (p.hermanos || 1) === hermanosActivo) : delPeriodo;

  if (planes.length === 0)
    return (
      <p className="text-sm text-muted-foreground" data-testid="portal-planes-empty">
        Tu sede aún no tiene planes de pago publicados.
      </p>
    );

  return (
    <div className="space-y-6" data-testid="portal-planes">
      <div className="flex flex-wrap gap-2" data-testid="portal-planes-grupos">
        {disponibles.map((g) => {
          const Icono = g.icono;
          return (
            <Chip key={g.id} activo={g.id === grupoActivo} data-testid={`portal-grupo-${g.id}`}
              onClick={() => { setGrupo(g.id); setPeriodo(""); setHermanos(0); }}>
              <span className="inline-flex items-center gap-2">
                <Icono className="h-4 w-4" /> {t(`portal.grupo.${g.id}`)}
              </span>
            </Chip>
          );
        })}
      </div>

      {segmentaPorHijos && (grupoActivo === "suscripciones" || tallas.length > 1) && (
        <div className="rounded-2xl border border-border p-4 space-y-4 bg-muted/30">
          {grupoActivo === "suscripciones" && (
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                {t("portal.periodicidad")}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="portal-planes-periodos">
                {periodos.map((tp) => (
                  <Chip key={tp} activo={tp === periodoActivo} data-testid={`portal-periodo-${tp}`}
                    onClick={() => { setPeriodo(tp); setHermanos(0); }}>
                    {t(`portal.periodo.${tp}`)}
                  </Chip>
                ))}
              </div>
            </div>
          )}
          {tallas.length > 1 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                {t("portal.cuantosHijos")}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="portal-planes-hermanos">
                {tallas.map((n) => (
                  <Chip key={n} activo={n === hermanosActivo} data-testid={`portal-hermanos-${n}`}
                    onClick={() => setHermanos(n)}>
                    {n === 1 ? t("portal.unHijo") : `${n} ${t("portal.hijos")}`}
                  </Chip>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {visibles.map((p) => <PlanCard key={p.id} p={p} pagando={pagando} onPagar={onPagar} />)}
      </div>
      {visibles.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="portal-planes-grupo-vacio">
          No hay planes en esta selección. Prueba otra periodicidad.
        </p>
      )}
    </div>
  );
};
